import { writable, get } from "svelte/store";
import { goto } from "$app/navigation";
import { browser } from "$app/environment";
import { SubscriptionAPI } from "$lib/api/subscription";
import { type AuthState, type User, type SubscriptionTier, SUBSCRIPTION_TIER } from "$lib/types";
import { userOnboardingStore } from "$lib/stores/userOnboardingStore";
import { searchPreferences } from "$lib/stores/searchPreferencesStore";
import { routes } from "$lib/routes";
import { page } from "$app/state";
import { sidePanelStore } from "$lib/stores/sidePanelStore";
import { identifyUser, resetAnalytics } from "$lib/utility/analytics";
import { TemplateAPI } from "$lib/api/template";
import { TableAPI } from "$lib/api/table";
import { AuthError } from "$lib/api/auth";
import posthog from "posthog-js";

declare global {
  const Rewardful: {
    referral: string;
  };
}
export interface AuthStore {
  subscribe: (this: void, run: (value: AuthState) => void) => () => void;
  shouldRedirectToCheckout: () => boolean;
  setSignInIntent: (redirectUrl: string) => void;
  getSignInIntent: () => { redirectUrl: string; createdAt: Date } | null;
  clearSignInIntent: () => void;
  initializeFromStorage: () => Promise<void>;
  initUser: () => Promise<void>;
  fetchUser: () => Promise<boolean | AuthError>;
  signin: (email: string, password: string) => Promise<boolean | AuthError>;
  signup: (email: string, password: string, confirmedPassword: string) => Promise<boolean | AuthError>;
  signout: () => Promise<void>;
  getAuthState: () => AuthState;
  getUserEmail: () => string | null;
  getUser: () => User | undefined;
  isLoggedIn: () => boolean;
  isAdmin: () => boolean;
  initiateGoogleSignin: () => void;
  checkAndUpdateAuthState: () => Promise<boolean>;
  updateUser: (userData: Partial<User>) => void;
  updateProModeAccess: () => Promise<boolean | AuthError>;
  updateUserPlan: () => Promise<void>;
  requestPasswordReset: (email: string) => Promise<boolean | AuthError>;
  checkPasswordResetToken: (token: string) => Promise<boolean | AuthError>;
  submitPasswordReset: (token: string, password: string, confirmedPassword: string) => Promise<boolean | AuthError>;
  initializeFromServer: (serverState: AuthState) => void;
  updateUserCredits: () => Promise<void>;
}

function getCurrentPlan(plans: { name: string; active: boolean }[]) {
  const activePlans = plans
    .filter((p) => p.active)
    .sort((a, b) => {
      const [aName, bName] = [a.name.toLowerCase(), b.name.toLowerCase()];
      // Team Annual = 5, Team Monthly = 4, Pro Annual = 3, Pro Monthly = 2, Plus Annual = 1, Plus Monthly = 0
      const getWeight = (name: string) =>
        (name.startsWith("team") ? 4 : name.startsWith("pro") ? 2 : 0) + (name.includes("annual") ? 1 : 0);
      return getWeight(bName) - getWeight(aName);
    });

  const topPlan = activePlans[0];
  return {
    userPlan: topPlan
      ? topPlan.name.toLowerCase().startsWith("team")
        ? SUBSCRIPTION_TIER.TEAM
        : topPlan.name.toLowerCase().startsWith("pro")
          ? SUBSCRIPTION_TIER.PRO
          : SUBSCRIPTION_TIER.PLUS
      : SUBSCRIPTION_TIER.FREE,
    userPlanLabel: topPlan?.name || "",
  };
}

function createAuthStore() {
  const { subscribe, set, update } = writable<AuthState>({
    isLoggedIn: false,
    user: undefined,
  });

  async function handleServerResponse(response: Response) {
    const contentType = response.headers.get("content-type");
    let data;

    if (contentType && contentType.includes("application/json")) {
      data = await response.json();
      // logger.log("Server response:", data);
    } else {
      throw new AuthError("Unexpected server response", response.status);
    }

    if (!response.ok) {
      let errorMessage = "An unexpected error occurred";

      const serverMessage = typeof data.message === "object" ? data.message.message : data.message;

      switch (response.status) {
        case 400:
          errorMessage = serverMessage || "Bad request";
          break;
        case 401:
          errorMessage = serverMessage || "Invalid email or password";
          break;
        case 403:
          errorMessage = serverMessage || "You do not have permission to perform this action.";
          break;
        case 404:
          errorMessage = serverMessage || "Resource not found";
          break;
        case 422:
          errorMessage = serverMessage || "Validation failed. Please check your input.";
          break;
        case 500:
          errorMessage = serverMessage || "Server error. Please try again later.";
          break;
        default:
          errorMessage = serverMessage || `Server error: ${response.status}`;
      }

      throw new AuthError(errorMessage, response.status);
    }

    return data;
  }

  function saveToStorage(state: AuthState): void {
    if (browser) {
      localStorage.setItem("auth", JSON.stringify(state));
    }
  }

  function loadFromStorage(): AuthState | null {
    if (browser) {
      const stored = localStorage.getItem("auth");
      if (stored) {
        try {
          return JSON.parse(stored);
        } catch (e) {
          console.error("Failed to parse stored auth state:", e);
        }
      }
    }
    return null;
  }

  function resetAuthState() {
    searchPreferences.reset();
    clearStorage();
    set({
      isLoggedIn: false,
      user: undefined,
      signInIntent: undefined,
    });
    resetAnalytics();
  }

  function clearStorage(): void {
    if (browser) {
      localStorage.removeItem("auth");
    }
  }

  function setSignInIntent(redirectUrl: string) {
    logger.log("Setting sign in intent:", redirectUrl);
    update((state) => ({
      ...state,
      signInIntent: {
        redirectUrl,
        createdAt: new Date(),
      },
    }));
    saveToStorage(get({ subscribe }));
  }

  function getSignInIntent() {
    const state = get({ subscribe });
    if (!state.signInIntent) return null;

    const minutesDifference = (new Date().getTime() - new Date(state.signInIntent.createdAt).getTime()) / (1000 * 60);

    if (minutesDifference > 30) {
      update((state) => ({
        ...state,
        signInIntent: undefined,
      }));
      saveToStorage(get({ subscribe }));
      return null;
    }

    return state.signInIntent;
  }

  function clearSignInIntent() {
    update((state) => ({
      ...state,
      signInIntent: undefined,
    }));
    saveToStorage(get({ subscribe }));
  }

  function shouldRedirectToCheckout(): boolean {
    const currentUser = get({ subscribe }).user;
    const signUpIntent = get(userOnboardingStore);
    const currentUserPlan = currentUser?.plan;
    const intentPlan = signUpIntent.plan as SubscriptionTier;

    return (currentUserPlan === SUBSCRIPTION_TIER.FREE || currentUserPlan === undefined) && intentPlan !== undefined;
  }

  async function handlePostSignupFlow(): Promise<void> {
    logger.log("Handling post-signup flow");
    const signUpIntent = get(userOnboardingStore);
    const currentUser = get({ subscribe }).user;

    // logger.log('Handling post-signup flow:', signUpIntent, currentUser);
    if (currentUser && signUpIntent.plan && signUpIntent.updatedAt) {
      const now = new Date();
      const timeDifference = now.getTime() - signUpIntent.updatedAt.getTime();
      const minutesDifference = timeDifference / (1000 * 60);

      // logger.log("Time difference in minutes:", minutesDifference);

      // Check if sign up intent is still valid (within 20 minutes)
      if (minutesDifference <= 20) {
        // logger.log('Intent is still valid:', signUpIntent);
        const intentPlan = signUpIntent.plan;
        const shouldUpgrade = shouldRedirectToCheckout();

        // logger.log('Should upgrade:', shouldUpgrade);

        if (shouldUpgrade) {
          const referral = Rewardful ? Rewardful.referral : "";
          await SubscriptionAPI.checkout(intentPlan, referral);
        } else {
          // If origin page exists, handle navigation
          // const originPage = userOnboardingStore.getOriginPage();
          // if (originPage && originPage.startsWith('/search/')) {
          //   goto(originPage);
          // } else {
          goto("/");
          // }
        }
      }
    }

    // Clear the sign up intent regardless of the outcome
    userOnboardingStore.reset();
  }

  return {
    subscribe,
    shouldRedirectToCheckout,
    setSignInIntent,
    getSignInIntent,
    clearSignInIntent,
    initializeFromStorage: async () => {
      const stored = loadFromStorage();
      if (stored) {
        const maxRetries = 1;
        const baseDelay = 1000;

        for (let attempt = 0; attempt < maxRetries; attempt++) {
          try {
            const statusResponse = await fetch("/api/v1/auth/status", {
              credentials: "include",
            });

            if (statusResponse.ok) {
              if (!stored.user?.id) {
                const userResponse = await fetch("/api/v1/auth/user", {
                  method: "GET",
                  headers: {
                    "Content-Type": "application/json",
                  },
                  credentials: "include",
                });

                if (userResponse.ok) {
                  const userData = await userResponse.json();
                  const newState: AuthState = {
                    isLoggedIn: true,
                    user: {
                      id: userData.userId,
                      email: userData.email,
                    },
                    signInIntent: stored.signInIntent,
                  };
                  set(newState);
                  saveToStorage(newState);
                  return;
                }
              } else {
                set(stored);
                return;
              }
            } else {
              const delay = baseDelay * Math.pow(2, attempt);
              await new Promise((resolve) => setTimeout(resolve, delay));
            }
          } catch (_error) {
            const delay = baseDelay * Math.pow(2, attempt);
            await new Promise((resolve) => setTimeout(resolve, delay));
          }
        }

        const currentState = get({ subscribe });
        set({
          isLoggedIn: false,
          user: undefined,
          signInIntent: currentState.signInIntent,
        });
        clearStorage();
      }
    },

    initUser: async (): Promise<void> => {
      try {
        const isPendingAuth = browser && localStorage.getItem("pendingGoogleAuth");
        const isStripeRedirect = browser && page.url.searchParams.get("stripe_success") === "true";
        let currentState = get({ subscribe });

        if (isPendingAuth) {
          // logger.log('[AuthStore] Handling pending Google auth...');
          localStorage.removeItem("pendingGoogleAuth");

          const storedState = loadFromStorage();
          const storedIntent = storedState?.signInIntent;

          // logger.log('[AuthStore] Stored intent during Google auth:', storedIntent);

          const success = await authStore.checkAndUpdateAuthState();
          if (!success) {
            resetAuthState();
            goto(routes.signIn);
            return;
          }

          const fetchResult = await authStore.fetchUser();
          if (fetchResult instanceof Error) {
            resetAuthState();
            goto(routes.signIn);
            return;
          }

          await Promise.all([authStore.updateUserPlan(), authStore.updateUserCredits()]);

          currentState = get({ subscribe });

          if (storedIntent) {
            update((state) => ({
              ...state,
              signInIntent: storedIntent,
            }));
            saveToStorage(get({ subscribe }));
          }

          const intent = getSignInIntent();

          if (intent) {
            // logger.log("[AuthStore] Redirecting to intent after Google auth:", intent.redirectUrl);
            clearSignInIntent();

            if (browser) {
              window.location.replace(intent.redirectUrl);
              return new Promise<void>(() => {});
            }
            return;
          }

          if (browser && window.location.pathname === "/") {
            goto(routes.tables);
          }
          return;
        }

        // update user state after successful redirect from stripe checkout
        if (isStripeRedirect) {
          await authStore.fetchUser();
          await Promise.all([authStore.updateUserPlan(), authStore.updateUserCredits()]);
          return;
        }

        await authStore.initializeFromStorage();
        currentState = get({ subscribe });

        if (currentState.isLoggedIn) {
          const sessionValid = await authStore.checkAndUpdateAuthState();
          if (sessionValid) {
            if (!currentState.user?.email || !currentState.user?.id) {
              await authStore.fetchUser();
            } else {
              posthog.setPersonPropertiesForFlags({ showBetaFeatures: "false" });
              // Identify user in PostHog during initialization
              identifyUser(currentState.user.id, {
                email: currentState.user.email,
                plan: currentState.user.plan,
                proModeAccess: currentState.user.proModeAccess,
              });
            }
            await Promise.all([authStore.updateUserPlan(), authStore.updateUserCredits()]);
          } else {
            resetAuthState();
          }
        }
      } catch (error) {
        console.error("Error during auth initialization:", error);
        resetAuthState();
      }
    },

    fetchUser: async (): Promise<boolean | AuthError> => {
      try {
        const response = await fetch("/api/v1/auth/user", {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
          },
          credentials: "include",
        });

        const data = await handleServerResponse(response);

        const { userPlan, userPlanLabel: _userPlanLabel } = getCurrentPlan(data.plans);

        const newState: AuthState = {
          isLoggedIn: true,
          user: {
            id: data.userId,
            email: data.email,
            plan: userPlan as SubscriptionTier,
            plans: data.plans,
            planLabel: data.plan,
            proModeAccess: data.proModeAccess,
            proModeRemaining: data.proModeRemaining,
            credits: data.credits || {
              total: 0,
              recurring: 0,
              topup: 0,
            },
          },
        };

        // Identify user in PostHog
        identifyUser(data.userId, {
          email: data.email,
          plan: userPlan,
          proModeAccess: data.proModeAccess,
        });

        if (userPlan === "pro") {
          searchPreferences.updatePreference("proMode", true);
          searchPreferences.updatePreference("searchAlgoVersion", "v3.5");
        }
        saveToStorage(newState);
        set(newState);
        return true;
      } catch (error) {
        return error as AuthError;
      }
    },

    signin: async (email: string, password: string): Promise<boolean | AuthError> => {
      try {
        const originalIntent = getSignInIntent();
        logger.log("[AuthStore] Starting sign in with intent:", originalIntent);

        const response = await fetch("/api/v1/auth/signin", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ email, password }),
          credentials: "include",
        });

        const data = await handleServerResponse(response);

        if (data.success) {
          const userResponse = await fetch("/api/v1/auth/user", {
            method: "GET",
            headers: { "Content-Type": "application/json" },
            credentials: "include",
          });
          const userData = await handleServerResponse(userResponse);

          const newState: AuthState = {
            isLoggedIn: true,
            user: {
              id: userData.userId,
              email: userData.email,
              plan: userData.plan,
              plans: userData.plans,
              proModeAccess: userData.proModeAccess,
              proModeRemaining: userData.proModeRemaining,
            },
            signInIntent: getSignInIntent() || undefined,
          };

          identifyUser(userData.userId, {
            email: userData.email,
            plan: userData.plan,
            proModeAccess: userData.proModeAccess,
          });

          if (userData.plan === "pro") {
            searchPreferences.updatePreference("proMode", true);
            searchPreferences.updatePreference("searchAlgoVersion", "v3.5");
          }

          set(newState);
          saveToStorage(newState);
          logger.log("Signin successful");

          if (browser) {
            const redirectUrl = newState.signInIntent?.redirectUrl;
            if (redirectUrl && window.location.pathname !== redirectUrl) {
              logger.log("Redirecting to:", redirectUrl);
              logger.log("Current path:", window.location.pathname);
              window.location.replace(redirectUrl);
            }
            return new Promise<boolean>(() => {});
          }
        }
        return false;
      } catch (error) {
        return error as AuthError;
      }
    },

    signup: async (email: string, password: string, confirmedPassword: string): Promise<boolean | AuthError> => {
      try {
        const originalIntent = getSignInIntent();
        logger.log("[AuthStore] Starting signup with intent:", originalIntent);

        let referralCode = "";
        if (browser) {
          const cookies = document.cookie.split(";");
          for (let i = 0; i < cookies.length; i++) {
            const cookie = cookies[i].trim();
            if (cookie.startsWith("referral_code=")) {
              referralCode = decodeURIComponent(cookie.substring("referral_code=".length));
              break;
            }
          }

          if (!referralCode) {
            try {
              const storedCode = localStorage.getItem("referral_code");
              referralCode = storedCode || "";
            } catch (e) {
              console.warn("Could not read referral code from localStorage:", e);
            }
          }
        }

        const payload = {
          email,
          password,
          confirmedPassword,
          referralCode,
        };

        const response = await fetch("/api/v1/auth/signup", {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(payload),
          credentials: "include",
        });

        const data = await handleServerResponse(response);

        if (browser && referralCode) {
          document.cookie = "referral_code=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/;SameSite=Lax";
          try {
            localStorage.removeItem("referral_code");
          } catch (e) {
            console.warn("Could not remove referral code from localStorage:", e);
          }
        }

        if (data.success) {
          const userResponse = await fetch("/api/v1/auth/user", {
            method: "GET",
            headers: {
              "Content-Type": "application/json",
            },
            credentials: "include",
          });
          const userData = await handleServerResponse(userResponse);

          const newState: AuthState = {
            isLoggedIn: true,
            user: {
              id: userData.userId,
              email: userData.email,
              plan: userData.plan,
              plans: userData.plans,
              proModeAccess: userData.proModeAccess,
              proModeRemaining: userData.proModeRemaining,
            },
          };

          localStorage.setItem("isNewSignup", "true");
          localStorage.setItem("productTourCompleted", "false");

          const prePopulateTemplates = await TemplateAPI.getTemplates({
            populateOnSignup: true,
          });

          if (prePopulateTemplates.length > 0) {
            for (const template of prePopulateTemplates) {
              await TableAPI.createTable({
                name: "Example: " + template.title,
                numColumns: 10,
                numRows: 10,
                templateId: template.id,
              });
            }
          }

          await Promise.all([
            authStore.updateUserPlan(),
            authStore.updateUserCredits(),
            (async () => {
              try {
                await identifyUser(userData.userId, {
                  email: userData.email,
                  plan: userData.plan,
                  proModeAccess: userData.proModeAccess,
                });
              } catch (e) {
                console.warn("Analytics identification failed:", e);
              }
            })(),
          ]);

          set(newState);
          saveToStorage(newState);

          logger.log("Signup successful");

          if (shouldRedirectToCheckout()) {
            logger.log("Redirecting to checkout");
            await handlePostSignupFlow();
          } else if (originalIntent) {
            logger.log("Redirecting to intent:", originalIntent.redirectUrl);
            clearSignInIntent();
            if (browser) {
              window.location.replace(originalIntent.redirectUrl);
              return new Promise<boolean>(() => {});
            }
          }
          return true;
        }
        return false;
      } catch (error) {
        return error as AuthError;
      }
    },

    signout: async () => {
      try {
        resetAuthState();

        const response = await fetch("/api/v1/auth/signout", {
          method: "POST",
          credentials: "include",
        });

        await handleServerResponse(response);
        localStorage.removeItem("isNewSignup");
        localStorage.removeItem("productTourCompleted");

        goto("/");
      } catch (error) {
        resetAuthState();
        console.error("Sign out failed:", error);

        goto("/");
      }
    },

    getAuthState: (): AuthState => {
      return get({ subscribe });
    },

    getUserEmail: (): string | null => {
      const state = get({ subscribe });
      return state.user?.email || null;
    },

    getUser: (): User | undefined => {
      const state = get({ subscribe });
      return state.user;
    },

    isLoggedIn: (): boolean => {
      const state = get({ subscribe });
      return state.user !== null && state.user !== undefined;
    },

    isAdmin(): boolean {
      const state = get({ subscribe });
      return state.user?.email?.endsWith("@flyx.ai") || false;
    },

    initiateGoogleSignin: () => {
      if (browser) {
        const currentState = get({ subscribe });
        saveToStorage(currentState);
        localStorage.setItem("pendingGoogleAuth", "true");
      }
      const googleAuthURL = "/api/v1/auth/google_oauth2";
      window.location.href = googleAuthURL;
    },

    checkAndUpdateAuthState: async (): Promise<boolean> => {
      try {
        const response = await fetch("/api/v1/auth/status", {
          credentials: "include",
        });

        const data = await handleServerResponse(response);

        if (data.success) {
          const userResponse = await fetch("/api/v1/auth/user", {
            method: "GET",
            headers: {
              "Content-Type": "application/json",
            },
            credentials: "include",
          });
          const userData = await handleServerResponse(userResponse);

          const currentState = get({ subscribe });
          const newState: AuthState = {
            isLoggedIn: true,
            user: {
              id: userData.userId,
              email: userData.email,
              displayName: userData.displayName,
              name: userData.name,
              picture: userData.picture,
              plans: userData.plans,
              proModeAccess: userData.proModeAccess,
              proModeRemaining: userData.proModeRemaining,
              plan: userData.plan,
            },
            signInIntent: currentState.signInIntent,
          };
          set(newState);
          saveToStorage(newState);
          return true;
        }

        if (response.status === 401) {
          resetAuthState();
          return false;
        }
        console.error("Failed to check auth status, keeping existing state");
        return get({ subscribe }).isLoggedIn;
      } catch (error) {
        console.error("Failed to check auth status, keeping existing state:", error);
        return get({ subscribe }).isLoggedIn;
      }
    },

    updateUser: (userData: Partial<User>) => {
      update((state) => {
        if (state.user) {
          const newState = {
            ...state,
            user: { ...state.user, ...userData },
          };
          saveToStorage(newState);
          return newState;
        }
        return state;
      });
    },

    updateProModeAccess: async (): Promise<boolean | AuthError> => {
      const currentState = get({ subscribe });
      if (!currentState.user) {
        return true;
      }
      try {
        const response = await fetch("/api/v1/auth/user", {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
          },
          credentials: "include",
        });

        const data = await handleServerResponse(response);

        const newState: AuthState = {
          ...currentState,
          user: {
            ...currentState.user,
            proModeAccess: data.proModeAccess,
            proModeRemaining: data.proModeRemaining,
          },
        };
        saveToStorage(newState);
        set(newState);
        return true;
      } catch (error) {
        return error as AuthError;
      }
    },

    updateUserPlan: async (): Promise<void> => {
      const currentState = get({ subscribe });

      if (!currentState.isLoggedIn || !currentState.user) {
        return;
      }
      try {
        const plans = await SubscriptionAPI.getProductPlans();

        const { userPlan, userPlanLabel } = getCurrentPlan(plans);

        update((state) => {
          if (state.user) {
            const newState = {
              ...state,
              user: {
                ...state.user,
                plan: userPlan,
                planLabel: userPlanLabel,
              },
            };
            saveToStorage(newState);
            return newState;
          }
          return state;
        });
      } catch (_error) {
        update((state) => {
          if (state.user) {
            const newState = {
              ...state,
              user: {
                ...state.user,
                plan: SUBSCRIPTION_TIER.FREE,
                planLabel: SUBSCRIPTION_TIER.FREE,
              },
            };
            saveToStorage(newState);
            return newState;
          }
          return state;
        });
      }
    },

    requestPasswordReset: async (email: string): Promise<boolean | AuthError> => {
      try {
        const response = await fetch("/api/v1/auth/password_reset", {
          method: "POST",
          headers: {
            "Content-Type": "application/x-www-form-urlencoded",
          },
          body: new URLSearchParams({ email }),
        });

        const data = await handleServerResponse(response);
        return data.success;
      } catch (error) {
        return error as AuthError;
      }
    },

    checkPasswordResetToken: async (token: string): Promise<boolean | AuthError> => {
      try {
        const response = await fetch(`/api/v1/auth/password_reset/check?token=${encodeURIComponent(token)}`, {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
          },
        });

        const data = await handleServerResponse(response);
        return data.success;
      } catch (error) {
        return error as AuthError;
      }
    },

    submitPasswordReset: async (
      token: string,
      password: string,
      confirmedPassword: string,
    ): Promise<boolean | AuthError> => {
      try {
        const response = await fetch("/api/v1/auth/password_reset/submit", {
          method: "POST",
          headers: {
            "Content-Type": "application/x-www-form-urlencoded",
          },
          body: new URLSearchParams({
            token,
            password,
            confirmedPassword,
          }),
        });

        const data = await handleServerResponse(response);

        return data.success;
      } catch (error) {
        return error as AuthError;
      }
    },

    initializeFromServer: (serverState: AuthState) => {
      set(serverState);
    },

    updateUserCredits: async (): Promise<void> => {
      const currentState = get({ subscribe });

      if (!currentState.user) {
        return;
      }

      try {
        const response = await fetch("/api/v1/billing/credits/balance", {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
          },
          credentials: "include",
        });

        const data = await handleServerResponse(response);
        logger.log("Fetched credits:", data);

        const totalCredits = (data.recurring || 0) + (data.topUp || 0);
        sidePanelStore.setCredits(totalCredits);

        update((state) => {
          if (state.user) {
            const newState = {
              ...state,
              user: {
                ...state.user,
                credits: {
                  total: totalCredits,
                  recurring: data.recurring || 0,
                  topup: data.topUp || 0,
                },
              },
            };
            saveToStorage(newState);
            return newState;
          }
          return state;
        });
      } catch (error) {
        console.error("Error fetching credits:", error);
        update((state) => {
          if (state.user) {
            const newState = {
              ...state,
              user: {
                ...state.user,
                credits: {
                  total: 0,
                  recurring: 0,
                  topup: 0,
                },
              },
            };
            saveToStorage(newState);
            return newState;
          }
          return state;
        });
      }
    },
  };
}

export const authStore = createAuthStore();
export { AuthError };
